import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import { Box, Flex, Text } from 'rebass/styled-components';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { isNetworkAdminSelector } from 'hooks/usePermissions/selectors';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { isSourcingAgentSelector, activeNetworkSelector } from 'redux/selectors';
import { JobsBulkActions } from '../jobsBulkActions';
import { JobsDataTable } from '../jobsDataTable';
import { SourcingBanner } from '../../molecules/sourcingBanner';
import strings from '../../../strings';
import { qsParse, qsStringify } from '../../../helpers/queryString';
import useJobs from '../../../hooks/useJobs';
import OrganizationSchema from '../../../schemas/organization';

const JobsTableWithFilters = ({ company, source, isCompanyProfile }) => {
  const history = useHistory();
  const hideCompanyFilter = useMemo(() => parseInt(company?.id, 10) === company?.id, [company]);
  const isNetworkAdmin = useSelector(isNetworkAdminSelector);
  const isSourcingAgent = useSelector(isSourcingAgentSelector);
  const activeNetwork = useSelector(activeNetworkSelector);

  const {
    jobs,
    totalJobs,
    isJobsLoading,
    hasJobsInitializationFailed,
    isJobsInitialized,
    loadNextPage,
    jobsFilters,
    hasJobsFilters,
    hasMoreJobs,
    statusOptions,
    visibilityOptions,
    expirationOptions,
    filterOrSortJobs,
    updateQueryString,
    initialSortBy,
    sourceOptions,
    seniorityOptions,
    featuredOptions,
    equityOptions,
  } = useJobs({ forCompany: company, includeMatchesLists: isNetworkAdmin, isCompanyProfile });
  const location = useLocation();
  const tableRef = useRef(null);
  const filtersKey = useMemo(() => `jobs-filters-visible-${activeNetwork?.id || 'default'}`, [activeNetwork?.id]);
  const [showFilters, setShowFilters] = useLocalStorage(filtersKey, false);
  const [isVisible, setIsVisible] = useLocalStorage(`sourcing-agent-banner-visible-${activeNetwork?.id}`, true);

  const filterCount = useMemo(() => {
    const currentFilters = Object.fromEntries(new URLSearchParams(location.search));
    const count = Object.keys(currentFilters)
      .filter((item) => !['q', 'sortBy', 'sortDirection', 'page'].includes(item))
      .filter((item) => currentFilters[item]).length;

    return count;
  }, [location.search]);

  const onResetFilters = useCallback(() => {
    filterOrSortJobs({ search: company ? { companyId: company.id } : {} });
  }, [filterOrSortJobs, company]);

  const onSort = useCallback(
    (c) => {
      const { id, desc } = c[0];
      const params = qsParse(location);
      params.sortBy = id;
      params.sortDirection = !desc ? 'asc' : 'desc';
      filterOrSortJobs({ search: params });
    },
    [filterOrSortJobs, location],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has('visibility')) {
      const currentParams = qsParse(location);
      currentParams.visibility = 'visible';
      history.replace({
        pathname: location.pathname,
        search: qsStringify(currentParams),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emptyView = useMemo(() => {
    if (hasJobsInitializationFailed) {
      return (
        <Flex width={1} justifyContent="center" mt={5}>
          <Text>{strings.genericError}</Text>
        </Flex>
      );
    }

    if (hasJobsFilters) {
      return (
        <Flex width={1} justifyContent="center" mt={5} flexDirection="column">
          <Text mx="auto" mb={[3]} fontWeight="semibold">
            No search results
          </Text>
          <Text mx="auto">There are no job openings with this criteria, try changing your filters.</Text>
        </Flex>
      );
    }

    return (
      <Flex width={1} justifyContent="center" mt={5}>
        <Text>There are no posted jobs for the network at this time</Text>
      </Flex>
    );
  }, [hasJobsFilters, hasJobsInitializationFailed]);

  return (
    <>
      {isSourcingAgent && isVisible && <SourcingBanner setIsVisible={setIsVisible} />}
      <Flex height="auto" sx={{ position: 'relative', bg: 'neutral.0' }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            minHeight: 'calc(100dvh - 203px)',
            transition: 'margin-left 0.3s ease-in-out',
          }}
        >
          <JobsBulkActions ref={tableRef} source={source} company={company}>
            <JobsDataTable
              onFiltersClicked={() => setShowFilters(!showFilters)}
              isCompanyProfile={isCompanyProfile}
              initialSortBy={initialSortBy}
              onSort={onSort}
              history={history}
              isJobsLoading={isJobsLoading}
              isJobsInitialized={isJobsInitialized}
              totalJobs={totalJobs}
              loadNextPage={loadNextPage}
              jobs={jobs}
              hasMoreJobs={hasMoreJobs}
              emptyView={emptyView}
              source={source}
              showListColumn={isNetworkAdmin}
              ref={tableRef}
              isSourcingAgent={isSourcingAgent}
              jobFilterProps={{
                toggleFilters: () => setShowFilters(!showFilters),
                jobsFilters,
                filterOrSortJobs,
                updateQueryString,
                sourceOptions,
                statusOptions,
                visibilityOptions,
                seniorityOptions,
                featuredOptions,
                expirationOptions,
                equityOptions,
                hideCompanyFilter,
                company,
                isCompanyProfile,
                filtersCount: filterCount,
                onResetFilters,
                showListFilters: showFilters,
                showFilters,
                isSourcingAgent,
              }}
            />
          </JobsBulkActions>
        </Box>
      </Flex>
    </>
  );
};

JobsTableWithFilters.propTypes = {
  company: OrganizationSchema,
  source: PropTypes.string,
  isCompanyProfile: PropTypes.bool,
};

JobsTableWithFilters.defaultProps = {
  company: null,
  source: 'jobs',
  isCompanyProfile: false,
};

export default JobsTableWithFilters;
